import { ERROR_CODES } from 'common/utils/constants';
import {
  createApiPolling,
  logger,
  reformatBeamRankData,
  reformatHeatmapData,
  getSuggestedHeatmapRange
} from 'common/utils/helpers';
import { ga2ProductMap } from 'components/features/CommissionGA2Modal/components/BeamSettingsForm/utils';
import { getDevice } from 'apis/GetDevice';
import { startRaster, checkRaster } from 'apis/Raster';
import { getRasterRecord } from 'apis/GetRasterRecord';
import { setDirection } from 'apis/SetDirection';
import { setServiceState } from 'apis/SetServiceState';
import { setCenterArfcn } from 'apis/commissioningApis/SetCenterArfcn';
import { getArfcnValues } from 'apis/commissioningApis/GetArfcnValues';
import { apiConfig } from 'config';
import { ActionTypes, GA2CommissioningSteps as Steps, GA2CommissioningStepStatuses as StepStatuses, defaultAcqInfo } from './constants';
import { setGa2LastSu } from 'apis/SetGa2LastSu';
import { getSingleDevice, updatePivotOverviewPageSingleSelectedDevice } from 'redux/actions';

const setDevice = createAction(ActionTypes.SET_DEVICE);
const setDeviceData = createAction(ActionTypes.SET_DEVICE_DATA);
const setAcqInfo = createAction(ActionTypes.SET_ACQ_INFO);
const setRaster = createAction(ActionTypes.SET_RASTER);
const setRasterStatus = createAction(ActionTypes.SET_RASTER_STATUS);
const setRasterData = createAction(ActionTypes.SET_RASTER_DATA);
const setAntennaDirections = createAction(ActionTypes.SET_ANTENNA_DIRECTIONS);
const setStep = createAction(ActionTypes.SET_STEP);
const setStepStatus = createAction(ActionTypes.SET_STEP_STATUS);
const setApplyError = createAction(ActionTypes.SET_APPLY_ERROR);
const clearApplyError = createAction(ActionTypes.CLEAR_APPLY_ERROR);
const setError = createAction(ActionTypes.SET_ERROR);
const setArfcnValues = createAction(ActionTypes.SET_ARFCN_VALUES);

export function initCommission(deviceId) {
  return async (dispatch) => {
    dispatch(setDevice(deviceId));

    try {
      const device = await getDevice(deviceId);

      dispatch(setDeviceData(device));
    } catch (error) {
      dispatch(setError(error));
    }
  };
}

export function goToNextCommissionStep() {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      data: {
        commissioningGA2: { currentStep }
      }
    } = state;

    switch (currentStep) {
      case Steps.BEAM_SETTINGS:
        return dispatch(goToAcquisitionInfoCommissionStep());
      case Steps.ACQUISITION_INFO:
        return dispatch(goToAcquisitionStatusCommissionStep());
      case Steps.ACQUISITION_STATUS:
        return dispatch(goToBeamSelectionCommissionStep());
      case Steps.BEAM_SELECTION:
        return dispatch(goToInServiceCommissionStep());
      case Steps.IN_SERVICE:
        return dispatch(startCommission());
      default:
        return;
    }
  };
}

export function goToAcquisitionInfoCommissionStep() {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      data: {
        commissioningGA2: { stepStatuses }
      }
    } = state;
    const status = stepStatuses[Steps.BEAM_SETTINGS];

    if (status === StepStatuses.PRISTINE) {
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.BEAM_SETTINGS));
      return dispatch(goToAcquisitionInfoCommissionStep());
    }

    if (status === StepStatuses.SUCCESS) {
      dispatch(setStep(Steps.ACQUISITION_INFO));
    }
  };
}

export function goToAcquisitionStatusCommissionStep() {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { stepStatuses }
      }
    } = getState();
    const status = stepStatuses[Steps.ACQUISITION_INFO];

    if (status === StepStatuses.PRISTINE) {
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.ACQUISITION_INFO));
      return dispatch(goToAcquisitionStatusCommissionStep());
    }

    if (status === StepStatuses.SUCCESS) {
      try {
        await dispatch(initRaster());
      } catch (error) {
        logger.error(error);
        dispatch(setError(error));
      } finally {
        dispatch(setStep(Steps.ACQUISITION_STATUS));
      }
    }
  };
}

export function goToBeamSelectionCommissionStep() {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      data: {
        commissioningGA2: { stepStatuses }
      }
    } = state;
    const status = stepStatuses[Steps.ACQUISITION_STATUS];

    if (status === StepStatuses.SUCCESS) {
      try {
        await dispatch(loadRasterData());
      } catch (error) {
        logger.error(error);
        dispatch(setError(error));
      } finally {
        dispatch(setStep(Steps.BEAM_SELECTION));
      }
    }
  };
}

export function goToInServiceCommissionStep() {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      data: {
        commissioningGA2: { stepStatuses, rasterData, antennaDirections }
      }
    } = state;
    const status = stepStatuses[Steps.BEAM_SELECTION];

    if (status === StepStatuses.PRISTINE) {
      const directions = antennaDirections ?? getDefaultAntennaDirections(rasterData);

      if (!directions) {
        // disable auto submit if no direction found
        dispatch(setStepStatus(StepStatuses.DIRTY, Steps.BEAM_SELECTION));
        return;
      }

      try {
        await dispatch(setCommissionDeviceAntennaDirections(directions));

        return dispatch(goToInServiceCommissionStep());
      } catch (error) {
        logger.error(error);
        dispatch(setError(error));
      }
    }

    if (status === StepStatuses.SUCCESS) {
      dispatch(setStep(Steps.IN_SERVICE));
    }
  };
}

export function startCommission() {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { stepStatuses }
      }
    } = getState();
    const status = stepStatuses[Steps.IN_SERVICE];

    if (status === StepStatuses.PRISTINE) {
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.IN_SERVICE));
      return dispatch(startCommission());
    }

    if (status === StepStatuses.SUCCESS) {
      try {
        await dispatch(putCommissionDeviceInService());

        dispatch(setDevice(null));
      } catch (error) {
        const errorObj = {
          title: 'Put Device in Service Error',
          message:
            error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
          type: error.cause?.status || 'Unknown'
        };
        dispatch(setApplyError(errorObj));
        dispatch(setError(error));
      }
    }
  };
}

export function resetAcqInfo() {
  return (dispatch) => {
    dispatch(setAcqInfo(defaultAcqInfo));
    dispatch(setStepStatus(StepStatuses.PRISTINE, Steps.ACQUISITION_INFO));
  };
}

export function terminateCommission() {
  return async (dispatch) => {
    // @todo clean up commission call
    dispatch(setDevice(null));
    dispatch(setAcqInfo(null));
  };
}

export function setCommissionDeviceBeamSettings({ centerFreq, bandwidth }) {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { deviceId }
      }
    } = getState();

    dispatch(setStepStatus(StepStatuses.PENDING, Steps.BEAM_SETTINGS));

    try {
      await setCenterArfcn({
        deviceId,
        centerFreq,
        bandwidth
      });

      const device = await getDevice(deviceId);
      
      dispatch(setDeviceData(device));
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.BEAM_SETTINGS));
    } catch (error) {
      const errorObj = {
        title: 'Set Center ARFCN Error',
        message: error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
        type: error.cause?.status || 'Unknown'
      };
      dispatch(setApplyError(errorObj));
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.BEAM_SETTINGS));
      throw error;
    }
  };
}

export function doGetArfcnValues(deviceId, productType) {
  return async (dispatch) => {
    try {
      const product = ga2ProductMap[productType];
      const result = await getArfcnValues(deviceId, product);
      dispatch(setArfcnValues(result));
    } catch (error) {
      logger.error(error);
      const errorObj = {
        title: 'Get ARFCN Values Error',
        message: error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
        type: error.cause?.status || 'Unknown'
      };
      dispatch(setApplyError(errorObj));
      throw error;
    }
  };
}

export function setCommissionDeviceAcquisition(acqInfo) {
  return async (dispatch) => {
    dispatch(setStepStatus(StepStatuses.PENDING, Steps.ACQUISITION_INFO));
    try {
      await dispatch(setAcqInfo(acqInfo));
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.ACQUISITION_INFO));
    } catch(error) {
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.ACQUISITION_INFO));
    };
  };
}

export function setCommissionDeviceAntennaDirections(directions) {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { deviceId }
      }
    } = getState();

    dispatch(setStepStatus(StepStatuses.PENDING, Steps.BEAM_SELECTION));

    try {
      const {
        // temporary set vertical directions equal horizontal
        downlink: { hpol, vpol = hpol }
      } = directions;
      await setDirection(
        {
          deviceId,
          directions: {
            downlink: {
              hpol,
              vpol
            }
          }
        },
        undefined,
        'v2'
      );

      dispatch(setAntennaDirections(directions));
      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.BEAM_SELECTION));
    } catch (error) {
      const errorObj = {
        message: error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
        type: error.cause?.status || 'Unknown'
      };
      dispatch(setApplyError(errorObj));
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.BEAM_SELECTION));
      throw error;
    }
  };
}

export function setDeviceAntennaDirections(deviceId, directions) {
  return async (dispatch) => {
    try {
      const {
        // temporary set vertical directions equal horizontal
        downlink: { hpol, vpol = hpol }
      } = directions;
      await setDirection(
        {
          deviceId,
          directions: {
            downlink: {
              hpol,
              vpol
            }
          }
        },
        undefined,
        'v2',
        ['Pending', 'Running']
      );

      await dispatch(
        getSingleDevice(deviceId, (result) => {
          dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
        })
      );
    } catch (error) {
      throw error;
    }
  };
}

export function setCommissionGa2LastSu(suId) {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { deviceId }
      }
    } = getState();

    dispatch(setStepStatus(StepStatuses.PENDING, Steps.IN_SERVICE));

    try {
      await setGa2LastSu(deviceId, suId);
      await dispatch(
        getSingleDevice(deviceId, (result) => {
          dispatch(setDeviceData(result));
        })
      );

      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.IN_SERVICE));
    } catch (error) {
      const errorObj = {
        message: error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
        type: error.cause?.status || 'Unknown'
      };
      dispatch(setApplyError(errorObj));
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.IN_SERVICE));
      throw error;
    }
  };
}

export function setCommissionDeviceGain() {
  return async (dispatch) => {
    dispatch(setStepStatus(StepStatuses.FAILURE, Steps.IN_SERVICE));
    // @todo set device gain
    throw new Error('Not implemented');
  };
}

export function putCommissionDeviceInService() {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { deviceId }
      }
    } = getState();
    dispatch(setStepStatus(StepStatuses.PENDING, Steps.IN_SERVICE));

    try {
      await setServiceState({
        deviceId,
        rfPath: 'all',
        serviceRequest: 'in-service-request'
      });

      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.IN_SERVICE));
    } catch (error) {
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.IN_SERVICE));
      throw error;
    }
  };
}

export function syncCommissionStepStatus(step, formState) {
  return (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { stepStatuses }
      }
    } = getState();
    const status = stepStatuses[step];
    const { isDirty } = formState;

    if (status === StepStatuses.PRISTINE && isDirty) {
      dispatch(setStepStatus(StepStatuses.DIRTY, step));
    } else if (status === StepStatuses.DIRTY && !isDirty) {
      dispatch(setStepStatus(StepStatuses.PRISTINE, step));
    }
  };
}

export function doClearApplyError() {
  return (dispatch) => {
    dispatch(clearApplyError());
  };
}

export function doSetStepStatus(status, step) {
  return (dispatch) => {
    dispatch(setStepStatus(status, step));
  }
}

function initRaster() {
  return async (dispatch, getState) => {
    let {
      data: {
        commissioningGA2: { deviceId, rasterId, acqInfo }
      }
    } = getState();

    if (rasterId) {
      return;
    }

    dispatch(setStepStatus(StepStatuses.PENDING, Steps.ACQUISITION_STATUS));

    try {
      // @todo check pending raster
      const payload = {
        source: 'commissioning',
        type: 'standard',
        ...(acqInfo && {
          configuration: {
            ...acqInfo
          }
        })
      };

      ({ rasterId } = await startRaster(deviceId, payload));

      dispatch(setRaster(rasterId));

      void (async () => {
        try {
          await dispatch(monitorRasterStatus(rasterId));
        } catch (error) {
          logger.error(error);
          dispatch(setError(error));
        }
      })();
    } catch (error) {
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.ACQUISITION_STATUS));
      throw error;
    }
  };
}

function loadRasterData() {
  return async (dispatch, getState) => {
    const {
      data: {
        commissioningGA2: { deviceId, rasterId }
      }
    } = getState();

    try {
      const data = await getRasterRecord(deviceId, rasterId);
      const { deviceVersion = 'ga2' } = data;
      const values = reformatHeatmapData(data.heatmap, deviceVersion);
      const beamrank = reformatBeamRankData(data.beamrank, data.heatmap, deviceVersion);
      const { min, max } = Object.values(data.heatmap).reduce((acc, value) => {
        acc.min = Math.min(acc.min, value);
        acc.max = Math.max(acc.max, value);

        return acc;
      }, getSuggestedHeatmapRange(deviceVersion));

      dispatch(
        setRasterData({
          values,
          beamrank,
          min: Math.floor(min / 5) * 5,
          max: Math.ceil(max / 5) * 5
        })
      );
    } catch (error) {
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.BEAM_SELECTION));
      throw error;
    }
  };
}

function monitorRasterStatus(rasterId) {
  return async (dispatch) => {
    try {
      await dispatch(pollRasterStatus(rasterId));

      dispatch(setStepStatus(StepStatuses.SUCCESS, Steps.ACQUISITION_STATUS));
    } catch (error) {
      dispatch(setStepStatus(StepStatuses.FAILURE, Steps.ACQUISITION_STATUS));
      throw error;
    }
  };
}

function pollRasterStatus(rasterId) {
  return async (dispatch, getState) => {
    const url = apiConfig.cmServiceStatusQueryGetUri(rasterId);
    const poll = createApiPolling(
      () => checkRaster({ url }),
      (instance) => {
        const {
          data: { commissioningGA2 }
        } = getState();

        if (commissioningGA2.rasterId !== rasterId) {
          return true;
        }

        if (!instance) {
          return false;
        }

        dispatch(setRasterStatus(instance));

        return !['pending', 'running'].includes(instance.runtimeStatus.toLowerCase());
      }
    );

    const result = await poll();

    if (result.runtimeStatus.toLowerCase() !== 'completed') {
      throw new Error('Failed to generate raster');
    }

    if (result.customStatus.status !== 'completed') {
      throw new Error(result.customStatus?.message ?? 'Failed to generate raster');
    }
  };
}

function createAction(type) {
  return (payload, meta = {}) => ({ type, payload, meta });
}

function getDefaultAntennaDirections(rasterData) {
  const [direction] = rasterData?.beamrank ?? [];

  if (!direction) {
    return null;
  }

  const { az: azimuth, el: elevation } = direction;

  return {
    downlink: {
      vpol: {
        azimuth,
        elevation
      },
      hpol: {
        azimuth,
        elevation
      }
    }
  };
}
